import React from 'react';
import {
  Box,
  Chip,
  Paper,
  Stack,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

/**
 * Soft semantic palette — colorful but standard admin UI.
 * Brand maroon stays primary; accents are teal / blue / amber / green / rose.
 */
export const tones = {
  maroon: {
    main: '#8B1538',
    soft: '#F8EEF1',
    border: 'rgba(139, 21, 56, 0.22)',
    text: '#6B0F2A',
  },
  teal: {
    main: '#0D9488',
    soft: '#E6F7F5',
    border: 'rgba(13, 148, 136, 0.28)',
    text: '#0F766E',
  },
  blue: {
    main: '#2563EB',
    soft: '#EEF4FF',
    border: 'rgba(37, 99, 235, 0.24)',
    text: '#1D4ED8',
  },
  amber: {
    main: '#D97706',
    soft: '#FFF7EB',
    border: 'rgba(217, 119, 6, 0.28)',
    text: '#B45309',
  },
  green: {
    main: '#159947',
    soft: '#ECF8F0',
    border: 'rgba(21, 153, 71, 0.28)',
    text: '#15803D',
  },
  rose: {
    main: '#E11D48',
    soft: '#FFF1F3',
    border: 'rgba(225, 29, 72, 0.24)',
    text: '#BE123C',
  },
  slate: {
    main: '#64748B',
    soft: '#F1F5F9',
    border: 'rgba(100, 116, 139, 0.28)',
    text: '#475569',
  },
};

export const billingSx = {
  page: {
    minHeight: '100%',
    px: { xs: 2, md: 3 },
    py: { xs: 2, md: 2.5 },
    background: 'linear-gradient(180deg, #FBF8F9 0%, #F3F6FA 48%, #F7F5F6 100%)',
  },
  panel: {
    bgcolor: 'background.paper',
    border: '1px solid',
    borderColor: 'rgba(139, 21, 56, 0.1)',
    borderRadius: 2,
    overflow: 'hidden',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
  },
  panelPad: {
    p: { xs: 2, md: 2.5 },
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 1.25,
    mb: 2,
  },
  tableHeadCell: {
    bgcolor: tones.maroon.soft,
    color: tones.maroon.text,
    fontWeight: 700,
    fontSize: '0.72rem',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    borderBottom: '1px solid',
    borderColor: tones.maroon.border,
    py: 1.25,
    whiteSpace: 'nowrap',
  },
  muted: { color: 'text.secondary', fontSize: '0.8125rem', lineHeight: 1.5 },
};

export function BillingPage({ children }) {
  return <Box sx={billingSx.page}>{children}</Box>;
}

export function BillingPanel({ children, sx, padded = false, tone }) {
  const t = tone ? tones[tone] : null;
  return (
    <Paper
      elevation={0}
      sx={{
        ...billingSx.panel,
        ...(padded ? billingSx.panelPad : null),
        ...(t
          ? {
              borderColor: t.border,
              borderTop: `3px solid ${t.main}`,
            }
          : null),
        ...sx,
      }}
    >
      {children}
    </Paper>
  );
}

export function BillingPageHeader({ title, subtitle, actions }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'space-between',
        gap: 2,
        mb: 2,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: tones.maroon.text,
            lineHeight: 1.25,
          }}
        >
          {title}
        </Typography>
        {subtitle ? (
          <Typography sx={{ ...billingSx.muted, mt: 0.5, maxWidth: 720 }}>{subtitle}</Typography>
        ) : null}
      </Box>
      {actions ? (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {actions}
        </Stack>
      ) : null}
    </Box>
  );
}

/** tone: maroon | teal | blue | amber | green | rose | slate */
export function BillingStat({ label, value, hint, tone = 'maroon', icon }) {
  const t = tones[tone] || tones.maroon;
  return (
    <Box
      sx={{
        height: '100%',
        px: 2.25,
        py: 2,
        borderRadius: 2,
        bgcolor: t.soft,
        border: '1px solid',
        borderColor: t.border,
        position: 'relative',
        overflow: 'hidden',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: `0 6px 16px ${t.border}`,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          right: -18,
          top: -18,
          width: 72,
          height: 72,
          borderRadius: '50%',
          bgcolor: t.main,
          opacity: 0.08,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Typography
          variant="caption"
          sx={{
            color: t.text,
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
          }}
        >
          {label}
        </Typography>
        {icon ? (
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              bgcolor: 'rgba(255,255,255,0.7)',
              color: t.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              '& .MuiSvgIcon-root': { fontSize: 18 },
            }}
          >
            {icon}
          </Box>
        ) : null}
      </Box>
      <Typography
        variant="h5"
        sx={{ mt: 1, fontWeight: 700, letterSpacing: '-0.02em', color: t.text }}
      >
        {value}
      </Typography>
      {hint ? (
        <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: t.text, opacity: 0.75 }}>
          {hint}
        </Typography>
      ) : null}
    </Box>
  );
}

const STATUS_COLORS = {
  ACTIVE: { bg: tones.green.soft, color: tones.green.text, border: tones.green.border },
  TRIAL: { bg: tones.blue.soft, color: tones.blue.text, border: tones.blue.border },
  GRACE_PERIOD: { bg: tones.amber.soft, color: tones.amber.text, border: tones.amber.border },
  SUSPENDED: { bg: tones.rose.soft, color: tones.rose.text, border: tones.rose.border },
  EXPIRED: { bg: tones.slate.soft, color: tones.slate.text, border: tones.slate.border },
  CANCELLED: { bg: tones.slate.soft, color: tones.slate.text, border: tones.slate.border },
  DRAFT: { bg: tones.maroon.soft, color: tones.maroon.text, border: tones.maroon.border },
  ISSUED: { bg: tones.blue.soft, color: tones.blue.text, border: tones.blue.border },
  PAID: { bg: tones.green.soft, color: tones.green.text, border: tones.green.border },
  OVERDUE: { bg: tones.rose.soft, color: tones.rose.text, border: tones.rose.border },
};

export function StatusChip({ status, label }) {
  const key = String(status || '').toUpperCase();
  const tone = STATUS_COLORS[key] || {
    bg: tones.slate.soft,
    color: tones.slate.text,
    border: tones.slate.border,
  };
  return (
    <Chip
      size="small"
      label={label || key || '—'}
      sx={{
        height: 24,
        fontWeight: 700,
        fontSize: '0.7rem',
        bgcolor: tone.bg,
        color: tone.color,
        border: `1px solid ${tone.border}`,
      }}
    />
  );
}

export function BillingTableHead({ columns }) {
  return (
    <TableHead>
      <TableRow>
        {columns.map((col) => (
          <TableCell
            key={col.id || col.label}
            align={col.align || 'left'}
            sx={{ ...billingSx.tableHeadCell, position: 'sticky', top: 0, zIndex: 1 }}
          >
            {col.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export const btnPrimary = {
  textTransform: 'none',
  fontWeight: 650,
  px: 2,
  boxShadow: 'none',
  '&:hover': { boxShadow: 'none' },
};
export const btnGhost = {
  textTransform: 'none',
  fontWeight: 600,
  px: 1.5,
  color: tones.maroon.text,
  borderColor: tones.maroon.border,
  bgcolor: 'rgba(255,255,255,0.7)',
  '&:hover': {
    borderColor: tones.maroon.main,
    color: tones.maroon.main,
    bgcolor: tones.maroon.soft,
  },
};

export default {
  BillingPage,
  BillingPanel,
  BillingPageHeader,
  BillingStat,
  StatusChip,
  BillingTableHead,
  billingSx,
  btnPrimary,
  btnGhost,
  tones,
};
